import classNames from 'classnames';
import React from 'react';

import DepthLayer from '~/components/patterns/utils/DepthLayer';
import {
	useWebsitesDropdownPosition,
} from '~/hooks/useWebsitesDropdownPosition';



type Props = {
	children: React.ReactNode,
	communityNavigation?: React.ReactNode,
	conductorNavigation?: React.ReactNode,
	header: React.ReactNode,
	logo?: React.ReactNode,
	menuExpanded?: boolean,
	menuMinimized?: boolean,
	monochrome?: boolean,
	navigationLayoutToggle?: React.ReactNode,
	newNavigation?: boolean,
	profile: React.ReactNode,
	userNavigation?: React.ReactNode,
	userNavigationDropdown?: React.ReactNode,
	websitesNavigation?: React.ReactNode,
	websitesNavigationDropdown?: React.ReactNode,
	websitesDropdownOpen?: boolean,
};



const ScreenSidebarLayout: React.FC<Props> = (props) => {
	const {
		children,
		communityNavigation,
		conductorNavigation,
		header,
		logo,
		menuExpanded = false,
		menuMinimized = false,
		monochrome,
		navigationLayoutToggle,
		newNavigation,
		profile,
		userNavigation,
		userNavigationDropdown,
		websitesNavigation,
		websitesNavigationDropdown,
		websitesDropdownOpen = false,
	} = props;

	const dropdownTargetRef = React.useRef<HTMLDivElement | null>(null);
	const dropdownTargetElementRef = React.useRef<HTMLDivElement | null>(null);

	// Position dropdown relative to WebsitesNavToggle for new navigation only
	useWebsitesDropdownPosition(dropdownTargetRef, newNavigation === true);

	// Additional cleanup effect to ensure styles are removed when switching to old navigation
	// This runs synchronously when newNavigation changes, before the hook's cleanup
	React.useEffect(() => {
		if (!newNavigation && dropdownTargetElementRef.current) {
			// Remove CSS custom properties
			dropdownTargetElementRef.current.style.removeProperty('--dropdown-top');
			dropdownTargetElementRef.current.style.removeProperty('--dropdown-left');
			dropdownTargetElementRef.current.style.removeProperty('--dropdown-width');

			// Remove ALL inline styles that were set for new navigation
			dropdownTargetElementRef.current.style.removeProperty('top');
			dropdownTargetElementRef.current.style.removeProperty('left');
			dropdownTargetElementRef.current.style.removeProperty('width');
			dropdownTargetElementRef.current.style.removeProperty('position');
			dropdownTargetElementRef.current.style.removeProperty('max-width');
			dropdownTargetElementRef.current.style.removeProperty('right');
		}
	}, [newNavigation]);

	// Store element reference for cleanup when switching navigation modes
	const handleDropdownTargetRef = React.useCallback((element: HTMLDivElement | null) => {
		dropdownTargetElementRef.current = element;
		if (newNavigation === true) {
			dropdownTargetRef.current = element;
		} else {
			// CRITICAL: Clean up ALL inline styles BEFORE setting ref to null
			// This ensures old navigation dropdown works correctly
			if (element) {
				// Remove CSS custom properties
				element.style.removeProperty('--dropdown-top');
				element.style.removeProperty('--dropdown-left');
				element.style.removeProperty('--dropdown-width');

				// Remove ALL inline styles that were set for new navigation
				// These MUST be cleared so old navigation's relative positioning works
				element.style.removeProperty('top');
				element.style.removeProperty('left');
				element.style.removeProperty('width');
				element.style.removeProperty('position');
				element.style.removeProperty('max-width');
				element.style.removeProperty('right');
			}

			dropdownTargetRef.current = null;
		}
	}, [newNavigation]);

	const componentClasses = classNames({
		'sidebar': true,
		'sidebar--conductor': conductorNavigation !== undefined,
		'sidebar--menu-expanded': menuExpanded === true,
		'sidebar--menu-minimized': menuMinimized === true,
		'sidebar--monochrome': monochrome,
		'sidebar--new-navigation': newNavigation === true,
		'sidebar--dropdown-open': websitesDropdownOpen === true && newNavigation === true,
	});

	return (
		<DepthLayer depthJump={1500}>
			<div className={componentClasses}>
				{logo && (
					<div
						className="sidebar__logo"
						itemScope={true}
						itemType="http://schema.org/Organization"
						role="banner"
					>
						<div className="sidebar__logo-inner">
							{logo}
						</div>
					</div>
				)}
				<div className="sidebar__header">
					{header}
				</div>
				{websitesNavigation && (
					<div className="sidebar__websites-nav">
						{websitesNavigation}
					</div>
				)}
				{websitesNavigationDropdown && (
					<div
						className={classNames({
							'sidebar__websites-nav-dropdown-target': true,
							// Only apply new navigation modifier when new navigation is active
							'sidebar__websites-nav-dropdown-target--new-navigation': newNavigation === true,
						})}
						ref={handleDropdownTargetRef}
					>
						{websitesNavigationDropdown}
					</div>
				)}
				<div className="sidebar__content js-scrollable">
					{children}
					{navigationLayoutToggle && (
						<div className="sidebar__navigation-layout-toggle">
							{navigationLayoutToggle}
						</div>
					)}
				</div>

				{communityNavigation && (
					<div className="sidebar__community">
						{communityNavigation}
					</div>
				)}
				{userNavigation && (
					<div className="sidebar__user-nav">
						{userNavigation}
					</div>
				)}
				{userNavigationDropdown && (
					<div className="sidebar__user-nav-dropdown-target">
						{userNavigationDropdown}
					</div>
				)}
				<div className="sidebar__profile">
					{profile}
				</div>
				{conductorNavigation && (
					<div className="sidebar__conductor">
						{conductorNavigation}
					</div>
				)}
			</div>
		</DepthLayer>
	);
};



export default ScreenSidebarLayout;
